import { useQuery } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import type { z } from "zod";
import type { managedTenantContextSchema } from "@/lib/managed-contract";
import { tenantContextOptions } from "./queries";

type TenantAuthority = z.infer<typeof managedTenantContextSchema>;

/** Keep live permissions and data bound to the identity loaded by this route. */
export function useTenantAuthority() {
  const initial = useRouterState({ select: state => {
    const match = state.matches.find(item => item.routeId === "/_authed/tenants/$slug");
    return (match?.context as { authority?: TenantAuthority } | undefined)?.authority;
  } });
  const query = useQuery({
    ...tenantContextOptions(initial?.tenant.slug ?? ""),
    enabled: !!initial,
    refetchInterval: initial ? 30_000 : false,
  });
  const current = query.data ?? initial;
  const identityChanged = !!initial && !!current &&
    (current.tenant.id !== initial.tenant.id || current.tenant.slug !== initial.tenant.slug);
  // A replacement's grants must never authorize a page holding the old
  // tenant's form or cached data. The scope layout refreshes and remounts it.
  const authority = query.isError || identityChanged ? undefined : current;
  return { initial, authority, identityChanged, query };
}
